import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { API_ORIGIN } from "./apiConfig";

const ACCESS_KEY = "admin_access_token";
const REFRESH_KEY = "admin_refresh_token";
const REFRESH_URL = `${API_ORIGIN}/api/auth/refresh`;
// Renew this long before the access token expires
const REFRESH_AHEAD_SECONDS = 5 * 60;
const CHECK_INTERVAL_MS = 30 * 1000;

type RetryConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

const getExp = (token: string | null): number => {
  if (!token) return 0;
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp : 0;
  } catch {
    return 0;
  }
};

const isJwtLive = (token: string | null): boolean => getExp(token) > Date.now() / 1000;

// An expired access token is fine while the refresh token can still renew it
export const hasAdminSession = (accessToken: string | null): boolean =>
  isJwtLive(accessToken) || isJwtLive(localStorage.getItem(REFRESH_KEY));

// Shared so parallel 401s wait for one refresh call instead of each starting their own
let refreshInFlight: Promise<string> | null = null;

const requestNewAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) throw new Error("No refresh token");
  const { data } = await axios.post(REFRESH_URL, null, {
    headers: { Authorization: `Bearer ${refreshToken}` },
    _authRetried: true,
  } as RetryConfig);
  if (!data?.access_token) throw new Error("Refresh failed");
  localStorage.setItem(ACCESS_KEY, data.access_token);
  if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
  return data.access_token;
};

const refreshOnce = (): Promise<string> => {
  refreshInFlight = refreshInFlight || requestNewAccessToken().finally(() => (refreshInFlight = null));
  return refreshInFlight;
};

const endSession = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  if (window.location.pathname.startsWith("/admin")) {
    sessionStorage.setItem("admin_session_expired", "1");
    window.location.assign("/admin");
  }
};

export function installAuthInterceptor() {
  axios.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined;
    const auth = config?.headers?.get("Authorization");
    const sentToken = typeof auth === "string" && auth.startsWith("Bearer ");

    if (error.response?.status !== 401 || !config || config._authRetried || !sentToken) {
      return Promise.reject(error);
    }
    config._authRetried = true;

    try {
      const token = await refreshOnce();
      config.headers.set("Authorization", `Bearer ${token}`);
      return axios.request(config);
    } catch {
      endSession();
      return Promise.reject(error);
    }
  });
}

let autoRefreshStarted = false;

// Keeps a logged-in admin's access token fresh so requests never hit an expired token
export function startTokenAutoRefresh() {
  if (autoRefreshStarted) return;
  autoRefreshStarted = true;

  const check = async () => {
    const access = localStorage.getItem(ACCESS_KEY);
    const refresh = localStorage.getItem(REFRESH_KEY);
    if (!access || !refresh) return;

    const now = Date.now() / 1000;
    if (getExp(access) - now > REFRESH_AHEAD_SECONDS) return;
    if (!isJwtLive(refresh)) {
      endSession();
      return;
    }
    try {
      await refreshOnce();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) endSession();
    }
  };

  check();
  window.setInterval(check, CHECK_INTERVAL_MS);
  // Background tabs throttle timers, so check again as soon as the admin returns
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
}
